import { Button, Heading, Icon, Stack, Table, Tbody, Td, Text, Th, Tr, useDisclosure } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { TrackingShipmentStatus } from '@prisma/client';
import { format } from 'date-fns';
import { HiCheckCircle, HiExclamationTriangle } from 'react-icons/hi2';

import { TrpcOutputs } from '../../config/trpc';
import { ShipmentContextProvider, useTrackingInfo } from '../../context/TrackingInfo';
import { ShipmentDrawer } from '../Drawers/ShipmentDrawer';
import { LabeledText } from '../LabeledText';

const TrackingShipmentsTable = ({ onOpen }: { onOpen: () => void }) => {
  const renderSubscriber = (
    subscriber: TrpcOutputs['getShipmentSubscribers']['subscribed'][0],
    isUnsubscribed: boolean,
  ) => {
    return (
      <LabeledText
        isHorizontal
        key={subscriber.id}
        label={subscriber.preferred_name ?? subscriber.full_name}
        value={
          <Stack direction="row" spacing={0} alignItems="center">
            <Icon
              w="20px"
              as={isUnsubscribed ? HiExclamationTriangle : HiCheckCircle}
              color={isUnsubscribed ? 'red' : 'green'}
            />
            <Text>{subscriber.email}</Text>
          </Stack>
        }
      />
    );
  };
  const { request, subscribers } = useTrackingInfo();

  return (
    <>
      <LabeledText
        label="Cutoff At"
        value={request?.final_update_at ? format(request?.final_update_at, 'yyyy-MM-dd') : ''}
        isHorizontal
      />
      <Button
        variant="ghost"
        colorScheme="primary"
        size="xs"
        position="absolute"
        right="4"
        top="4"
        onClick={onOpen}
        isDisabled={!request?.booked_quote_variant_id}
      >
        Manage
      </Button>
      <Stack direction="column" spacing={2}>
        <Heading fontSize="sm" ml="0">
          Subscribers
        </Heading>
        <Show if={!!request?.booked_quote_variant_id}>
          <>
            <Stack direction="column" spacing={0}>
              {subscribers?.subscribed?.map((s) => renderSubscriber(s, false))}
              {subscribers?.unsubscribed?.map((s) => renderSubscriber(s, true))}
            </Stack>
            <Show if={subscribers?.subscribed?.length && !subscribers?.unsubscribed?.length}>
              <Text fontSize="sm" color="gray.500">
                Nobody is subscribed.
              </Text>
            </Show>
          </>
        </Show>
      </Stack>
      <Table variant="extraCompact">
        <Tbody>
          <Tr>
            <Th>Service</Th>
            <Th>Tracking #</Th>
            <Th>Events</Th>
          </Tr>
          {request?.tracking_shipments?.map((shipment) => (
            <Tr
              key={shipment.id}
              fontSize="sm"
              textDecoration={shipment?.status !== TrackingShipmentStatus.ACTIVE ? 'line-through' : undefined}
            >
              <Td>{shipment.tracking_handler}</Td>
              <Td>{shipment.tracking_number}</Td>
              <Td>{shipment.events?.length || 0}</Td>
            </Tr>
          ))}
          <Show if={!request?.tracking_shipments?.length}>
            <Text fontSize="sm" color="gray.500">
              No shipments.
            </Text>
          </Show>
        </Tbody>
        <Show if={!request?.booked_quote_variant_id}>
          <Tbody>
            <Tr>
              <Td fontSize="sm" color="gray.500">
                Not booked
              </Td>
            </Tr>
          </Tbody>
        </Show>
      </Table>
    </>
  );
};

export const TrackingShipments = () => {
  const disclosure = useDisclosure();

  return (
    <ShipmentContextProvider>
      <Stack direction="column" spacing={4} px={2}>
        <ShipmentDrawer {...disclosure} />
        <TrackingShipmentsTable onOpen={disclosure.onOpen} />
      </Stack>
    </ShipmentContextProvider>
  );
};
